/**
 * TagEditor — inline tag picker for a task.
 *
 * Renders the task's current tags as removable chips and exposes a small
 * input that suggests existing tags from the project's pool, creating one
 * on-the-fly if the typed name doesn't match any. Used on TaskDetailPage to
 * close UAT 4.6 (tester couldn't find any way to add a tag).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
    getTags, createTag, addTagToTask, removeTagFromTask,
    type Tag,
} from '../packages/api-client/pm-api';
import { getErrorMessage } from '../packages/api-client';
import { hexAlpha } from '../utils/statusColors';

const NEW_TAG_COLORS = ['#2DE8D0', '#14867A', '#F5A623', '#F97316', '#F87171', '#C05CFF', '#3BA7FF', '#94A3B8'];

interface TagEditorProps {
    taskId: string;
    projectId: string;
    tags: Tag[];
    onTagsChanged: (tags: Tag[]) => void;
}

export function TagEditor({ taskId, projectId, tags, onTagsChanged }: TagEditorProps) {
    const [available, setAvailable] = useState<Tag[]>([]);
    const [input, setInput] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [busy, setBusy] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        getTags(projectId).then(setAvailable).catch(() => { /* non-fatal */ });
    }, [projectId]);

    // Click outside closes the suggestions dropdown.
    useEffect(() => {
        function onDocClick(e: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const trimmed = input.trim();
    const suggestions = useMemo(() => {
        const taken = new Set(tags.map(t => t.id));
        return available
            .filter(t => !taken.has(t.id))
            .filter(t => trimmed === '' || t.name.toLowerCase().includes(trimmed.toLowerCase()));
    }, [available, tags, trimmed]);

    const exactMatch = available.some(t => t.name.toLowerCase() === trimmed.toLowerCase());
    const canCreate = trimmed.length > 0 && !exactMatch;

    async function attach(tag: Tag) {
        setBusy(true);
        try {
            await addTagToTask(taskId, tag.id);
            onTagsChanged([...tags, tag]);
            setInput('');
            setShowSuggestions(false);
        } catch (err) {
            toast.error(getErrorMessage(err) || 'Could not add tag');
        } finally {
            setBusy(false);
        }
    }

    async function createAndAttach() {
        if (!trimmed) return;
        setBusy(true);
        try {
            const color = NEW_TAG_COLORS[Math.floor(Math.random() * NEW_TAG_COLORS.length)];
            const created = await createTag(projectId, trimmed, color);
            setAvailable(prev => [...prev, created]);
            await addTagToTask(taskId, created.id);
            onTagsChanged([...tags, created]);
            setInput('');
            setShowSuggestions(false);
        } catch (err) {
            toast.error(getErrorMessage(err) || 'Could not create tag');
        } finally {
            setBusy(false);
        }
    }

    async function detach(tagId: string) {
        setBusy(true);
        try {
            await removeTagFromTask(taskId, tagId);
            onTagsChanged(tags.filter(t => t.id !== tagId));
        } catch (err) {
            toast.error(getErrorMessage(err) || 'Could not remove tag');
        } finally {
            setBusy(false);
        }
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (suggestions.length > 0) {
                attach(suggestions[0]);
            } else if (canCreate) {
                createAndAttach();
            }
        } else if (e.key === 'Escape') {
            setInput('');
            setShowSuggestions(false);
        }
    }

    return (
        <div ref={containerRef} style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 'var(--space-1)', alignItems: 'center', position: 'relative' }}>
            {tags.map(tag => (
                <span
                    key={tag.id}
                    className="tag-chip"
                    style={{ background: hexAlpha(tag.color, 0.19), color: tag.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                    {tag.name}
                    <button
                        type="button"
                        onClick={() => detach(tag.id)}
                        disabled={busy}
                        aria-label={`Remove tag ${tag.name}`}
                        title={`Remove ${tag.name}`}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: 'inherit', opacity: 0.7, fontSize: '0.85em',
                            padding: 0, lineHeight: 1, marginLeft: 2,
                        }}
                    >
                        ×
                    </button>
                </span>
            ))}
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    className="form-input"
                    placeholder={tags.length === 0 ? '+ Add tag…' : '+ Tag'}
                    value={input}
                    onChange={e => { setInput(e.target.value); setShowSuggestions(true); }}
                    onFocus={() => setShowSuggestions(true)}
                    onKeyDown={handleKeyDown}
                    disabled={busy}
                    style={{
                        height: '2rem', padding: '0 8px', fontSize: 'var(--font-size-xs)',
                        width: input ? Math.max(120, input.length * 8 + 32) + 'px' : '110px',
                        minWidth: '110px',
                    }}
                />
                {showSuggestions && (suggestions.length > 0 || canCreate) && (
                    <div
                        style={{
                            position: 'absolute', top: '100%', left: 0, marginTop: 4,
                            background: 'var(--color-surface)', border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-md)',
                            minWidth: 180, maxHeight: 240, overflowY: 'auto', zIndex: 50,
                        }}
                    >
                        {suggestions.slice(0, 8).map(t => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => attach(t)}
                                disabled={busy}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    width: '100%', textAlign: 'left',
                                    padding: '6px 10px', background: 'none', border: 'none',
                                    cursor: 'pointer', fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text)',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-surface-2)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                            >
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.color }} />
                                {t.name}
                            </button>
                        ))}
                        {canCreate && (
                            <button
                                type="button"
                                onClick={createAndAttach}
                                disabled={busy}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    width: '100%', textAlign: 'left',
                                    padding: '6px 10px', background: 'none', border: 'none',
                                    cursor: 'pointer', fontSize: 'var(--font-size-sm)',
                                    color: 'var(--accent)',
                                    borderTop: suggestions.length > 0 ? '1px solid var(--color-border)' : 'none',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-surface-2)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                            >
                                + Create &ldquo;{trimmed}&rdquo;
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
